import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import * as Sentry from '@sentry/nuxt'
import { getApiErrorMessage } from '~/utils/getApiErrorMessage'
import {
  fetchWageringStatus,
  forfeitAllBonuses,
  type WageringStatus,
  type BlockingGrant,
} from '~/composables/api/useBonusApi'

/**
 * Bonus store — holds the player's active-bonus wagering state.
 *
 * Voucher validate/redeem are one-shot request/response flows owned by the
 * activation component, so they are NOT cached here. What IS shared app-wide
 * is the wagering status (active grants, progress, withdrawal-blocked flag),
 * because several surfaces read it: the wallet progress card, the withdrawal
 * warning modal, and the in-game min-cashout button block.
 */
export const useBonusStore = defineStore('bonus', () => {
  // State
  const status = ref<WageringStatus | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Dedup: coalesce concurrent fetches (wallet card + game panel can both ask).
  let inflight: Promise<void> | null = null

  // Getters
  const hasActiveBonus = computed(() => (status.value?.activeGrantCount ?? 0) > 0)
  const canWithdraw = computed(() => status.value?.canWithdraw ?? true)
  const blockingGrants = computed<BlockingGrant[]>(() => status.value?.blockingGrants ?? [])
  const totalBonusAtRisk = computed(() => parseFloat(status.value?.totalBonusAtRisk ?? '0'))
  const totalWageringRemaining = computed(() => parseFloat(status.value?.totalWageringRemaining ?? '0'))

  // Binding min cash-out multiplier for the in-game Cash Out gate. null = no
  // floor (either no active bonus, or the grant sets no min). While a bonus is
  // active bets are placed bonus-first, so this floor applies to the live bet.
  const minCashoutMultiplier = computed<number | null>(() => status.value?.minCashoutMultiplier ?? null)

  // Pending (not-yet-funded) deposit-match voucher, surfaced by the backend so
  // the deposit field can restore its activated/deactivate state after a reload
  // or in a new session (pending grants aren't in blockingGrants). null = none.
  const pendingDepositMatch = computed(() => status.value?.pendingDepositMatch ?? null)

  // Overall wagering progress across all active grants, weighted so a single
  // bar is honest even with multiple grants: sum(done) / sum(required).
  // Per-grant required/done in XAUT. Prefer the raw wageringRequired/Progress the
  // endpoint now sends; fall back to reconstructing from percentComplete +
  // remaining for an older payload. The raw path avoids the near-100% collapse
  // (reconstruction divides by 1 - pct/100, which rounds to 0 at the finish and
  // snapped the strip to "$0 of $0").
  function grantRequiredDone(g: BlockingGrant): { required: number; done: number } {
    const remaining = parseFloat(g.wageringRemaining) || 0
    if (g.wageringRequired != null) {
      const required = parseFloat(g.wageringRequired) || 0
      const done = g.wageringProgress != null
        ? (parseFloat(g.wageringProgress) || 0)
        : Math.max(0, required - remaining)
      return { required, done }
    }
    const pct = parseFloat(g.percentComplete) || 0
    const frac = 1 - pct / 100
    const required = frac > 0.0001 ? remaining / frac : remaining
    return { required, done: required - remaining }
  }

  const overallPercent = computed(() => {
    const grants = blockingGrants.value
    if (grants.length === 0) return 100
    let done = 0
    let required = 0
    for (const g of grants) {
      const rd = grantRequiredDone(g)
      required += rd.required
      done += rd.done
    }
    if (required <= 0) return 100
    return Math.min(100, Math.max(0, (done / required) * 100))
  })

  // Total wagering required across all active grants (XAUT). Used by the in-game
  // strip's "$X of $Y" label (Y = total to wager = bonus × multiplier). Format to
  // USD at the call site.
  const totalWageringRequired = computed(() => {
    let required = 0
    for (const g of blockingGrants.value) {
      required += grantRequiredDone(g).required
    }
    return required
  })

  // Actions
  async function fetchStatus(accessToken: string): Promise<void> {
    if (inflight) return inflight

    inflight = (async () => {
      isLoading.value = true
      error.value = null
      try {
        status.value = await fetchWageringStatus(accessToken)
      } catch (err) {
        // bonus-withdrawal-status fails open server-side, but a network error
        // here shouldn't wipe a previously-good status. Log and keep last.
        console.error('[bonusStore] Failed to fetch wagering status:', err)
        Sentry.captureException(err, { tags: { store: 'bonus', action: 'fetchStatus' } })
        error.value = getApiErrorMessage(err, 'Failed to fetch bonus status')
      } finally {
        isLoading.value = false
        inflight = null
      }
    })()
    return inflight
  }

  async function forfeit(accessToken: string, reason?: string): Promise<boolean> {
    isLoading.value = true
    error.value = null
    try {
      const res = await forfeitAllBonuses(accessToken, reason)
      // Optimistically clear the active-bonus state; a follow-up fetchStatus
      // (and balance refetch) confirms.
      status.value = {
        canWithdraw: true,
        activeGrantCount: 0,
        totalWageringRemaining: '0',
        totalBonusAtRisk: '0',
        blockingGrants: [],
      }
      return res.success
    } catch (err) {
      console.error('[bonusStore] Forfeit failed:', err)
      Sentry.captureException(err, { tags: { store: 'bonus', action: 'forfeit' } })
      error.value = getApiErrorMessage(err, 'Failed to forfeit bonus')
      return false
    } finally {
      isLoading.value = false
    }
  }

  function clear(): void {
    status.value = null
    error.value = null
  }

  return {
    // State
    status,
    isLoading,
    error,
    // Getters
    hasActiveBonus,
    canWithdraw,
    blockingGrants,
    totalBonusAtRisk,
    totalWageringRemaining,
    totalWageringRequired,
    minCashoutMultiplier,
    pendingDepositMatch,
    overallPercent,
    // Actions
    fetchStatus,
    forfeit,
    clear,
  }
})
